/**
 * SimilarLeadsModal — when a user opens a lead, show a curated list of
 * "similar leads" from the saved-leads collection. Lets the user quickly
 * pivot to alternative options if the seed lead doesn't fit.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Loader2, Layers, MapPin, Phone, Save, ExternalLink, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SimilarLeadsModal({ open, onClose, seed, onPick, onSave }) {
  const { t, lang } = useLang();
  const isPt = lang === "pt";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !seed) return undefined;
    setLoading(true);
    setItems([]);
    axios.post(`${API}/leads/similar`, {
      lead_id: seed.id,
      sector: seed.sector,
      address: seed.address,
      digital_score: seed.digital_score,
      limit: 10,
    })
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [open, seed]);

  if (!seed) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="similar-leads-modal"
        className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-[18px] font-bold flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#3B82F6]" />
            {isPt ? "Leads similares a" : "Similar leads to"}
            <span className="text-[#3B82F6] truncate">{seed.name}</span>
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#6B7280]">
            {isPt
              ? `Setor: ${seed.sector || "—"} · Cidade: ${(seed.address || "").split(",").slice(-2, -1)[0] || "—"} · Score: ${seed.digital_score ?? "—"}`
              : `Sector: ${seed.sector || "—"} · City: ${(seed.address || "").split(",").slice(-2, -1)[0] || "—"} · Score: ${seed.digital_score ?? "—"}`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-[#6B7280] text-[13px]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {isPt ? "Buscando similares..." : "Searching similar..."}
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-[#6B7280]">
            {isPt
              ? "Nenhum lead similar encontrado nos leads salvos. Faça mais buscas para popular este painel."
              : "No similar leads found in saved leads. Run more searches to populate this view."}
          </div>
        ) : (
          <div className="space-y-2 mt-2" data-testid="similar-leads-list">
            {items.map((ld) => {
              const badgeCls =
                ld.badge === "QUENTE" ? "bg-[#FEE2E2] text-[#DC2626]" :
                ld.badge === "FRIO" ? "bg-[#DCFCE7] text-[#16A34A]" :
                "bg-[#FFEDD5] text-[#C2410C]";
              return (
                <div
                  key={ld.id}
                  data-testid={`similar-lead-${ld.id}`}
                  className="border border-[#E5E7EB] rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 hover:border-[#3B82F6] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-[13.5px] text-[#111827] truncate">{ld.name}</h4>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${badgeCls}`}>
                        {ld.badge || "MORNO"}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-[#6B7280] truncate flex items-center gap-2">
                      <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {ld.address || "—"}</span>
                      {ld.phone && <span className="inline-flex items-center gap-0.5"><Phone className="w-3 h-3" /> {ld.phone}</span>}
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">
                      <span className="font-semibold text-[#111827]">{ld.sector || "—"}</span>
                      {" · "}{isPt ? "Score" : "Score"}: <span className="font-bold">{ld.digital_score ?? "—"}/100</span>
                      {ld.rating && (
                        <> · ★ {Number(ld.rating).toFixed(1)} ({ld.review_count || 0})</>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      data-testid={`similar-pick-${ld.id}`}
                      onClick={() => { onPick && onPick(ld); onClose(); }}
                      className="px-2.5 h-7 rounded text-[11px] font-bold bg-[#3B82F6] hover:bg-[#2563EB] text-white inline-flex items-center gap-1"
                    >
                      {isPt ? "Abrir" : "Open"}
                    </button>
                    {onSave && (
                      <button
                        type="button"
                        data-testid={`similar-save-${ld.id}`}
                        onClick={() => onSave(ld)}
                        className="px-2.5 h-7 rounded text-[11px] font-bold border border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF] inline-flex items-center gap-1"
                      >
                        <Save className="w-3 h-3" /> CRM
                      </button>
                    )}
                    {ld.google_maps_url && (
                      <a
                        href={ld.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 h-7 rounded text-[11px] font-bold border border-[#E5E7EB] hover:bg-[#FAFAFB] inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex justify-end pt-2 border-t border-[#E5E7EB]">
          <button
            type="button"
            data-testid="similar-close-btn"
            onClick={onClose}
            className="px-4 h-9 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold hover:bg-[#FAFAFB] inline-flex items-center gap-1.5"
          >
            <X className="w-3.5 h-3.5" /> {isPt ? "Fechar" : "Close"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
